import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import { errorCode, formatDate } from './cycleStatus'
import { changeText, markerValue, num } from './followUpFormat'
import { humanizeCode } from './healthReportFormat'

// VH-230: what improved, and why. Every number and label comes computed from the
// backend as a code; this tab only translates and lays them out.

const SUMMARY_KEYS = ['improved', 'worsened', 'unchanged', 'not_comparable', 'variation_unknown', 'new']

const DIRECTION_CLASS = {
  better: 'text-green-700',
  worse: 'text-red-700',
  unchanged: 'text-gray-700',
  not_comparable: 'text-amber-700',
  variation_unknown: 'text-gray-600',
}

function Card({ id, title, children }) {
  return (
    <article className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`followup-section-${id}`}>
      <h4 className="text-base font-semibold text-gray-900">{title}</h4>
      {children}
    </article>
  )
}

function Markers({ markers }) {
  const { t } = useTranslation()
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500">
            <th className="py-1 pr-3">{t('cycleReports.followup.columns.marker')}</th>
            <th className="py-1 pr-3">{t('cycleReports.followup.columns.baseline')}</th>
            <th className="py-1 pr-3">{t('cycleReports.followup.columns.new')}</th>
            <th className="py-1 pr-3">{t('cycleReports.followup.columns.change')}</th>
            <th className="py-1 pr-3">{t('cycleReports.followup.columns.range')}</th>
            <th className="py-1">{t('cycleReports.followup.columns.direction')}</th>
          </tr>
        </thead>
        <tbody>
          {markers.map((m) => (
            <tr key={m.marker_code} className="border-t border-gray-100 align-top" data-testid={`followup-marker-${m.marker_code}`}>
              <td className="py-1 pr-3 text-gray-900">{m.marker_name ?? humanizeCode(m.marker_code)}</td>
              <td className="py-1 pr-3">{markerValue(m.baseline)}</td>
              <td className="py-1 pr-3">{markerValue(m.new)} {m.unit}</td>
              <td className="py-1 pr-3">
                {changeText(m)}
                {m.rcv_pct != null && (
                  <span className="block text-xs text-gray-500">{t('cycleReports.followup.rcv', { value: m.rcv_pct })}</span>
                )}
              </td>
              <td className="py-1 pr-3">{`${num(m.reference_min)}–${num(m.reference_max)}`}</td>
              <td className="py-1">
                <span className={DIRECTION_CLASS[m.direction] ?? 'text-gray-700'}>
                  {t(`cycleReports.followup.direction.${m.direction}`)}
                </span>
                {(m.comparability_reasons ?? []).map((r, i) => (
                  <span key={i} className="block text-xs text-amber-700">
                    {t(`cycleReports.followup.reason.${r.code}`, { test: r.test ? t(`cycleReports.followup.test.${r.test}`) : '' })}
                  </span>
                ))}
                {(m.notes ?? []).map((n) => (
                  <span key={n} className="block text-xs text-gray-500">{t(`cycleReports.followup.note.${n}`)}</span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Adherence({ adherence }) {
  const { t } = useTranslation()
  const cov = adherence.coverage ?? {}
  const nutrition = adherence.nutrition
  const training = adherence.training
  return (
    <div className="space-y-3 text-sm text-gray-700">
      <p className={cov.sufficient ? 'text-gray-700' : 'text-amber-700'} data-testid="followup-coverage">
        {t(cov.sufficient ? 'cycleReports.followup.coverage' : 'cycleReports.followup.coverageLow', {
          pct: num(cov.share_pct), logged: num(cov.logged_days), days: num(cov.days), min: num(cov.minimum_pct),
        })}
      </p>
      {nutrition && (
        <div>
          <h5 className="font-medium text-gray-900">{t('cycleReports.followup.nutrition')}</h5>
          {nutrition.status !== 'computed' ? (
            <p>{t(`cycleReports.followup.nutritionStatus.${nutrition.status}`)}</p>
          ) : (
            <ul className="list-disc pl-5">
              {nutrition.targets.map((x) => (
                <li key={x.code}>
                  {t('cycleReports.followup.targetLine', {
                    target: t(`cycleReports.targetKey.${x.code}`), planned: num(x.planned), unit: x.unit ?? '',
                    average: num(x.logged_average), within: num(x.within_tolerance_pct), coverage: num(nutrition.coverage_label_pct),
                  })}
                </li>
              ))}
              {nutrition.item_limits.map((x) => (
                <li key={x.code}>
                  {t('cycleReports.followup.limitLine', {
                    limit: t(`cycleReports.limitCode.${x.code}`), planned: num(x.planned), unit: x.unit ?? '',
                    average: num(x.logged_average), within: num(x.within_limit_pct),
                  })}
                </li>
              ))}
            </ul>
          )}
          {nutrition.unplanned_weeks?.length > 0 && (
            <p className="text-xs text-gray-500">{t('cycleReports.followup.unplannedWeeks', { count: nutrition.unplanned_weeks.length })}</p>
          )}
        </div>
      )}
      {training && (
        <div>
          <h5 className="font-medium text-gray-900">{t('cycleReports.followup.training')}</h5>
          <p>
            {training.status === 'computed'
              ? t('cycleReports.followup.trainingLine', {
                planned: num(training.planned_sessions_total), completed: num(training.completed_sessions),
                sets: num(training.working_sets_per_session),
              })
              : t('cycleReports.followup.trainingNoPlan', { completed: num(training.completed_sessions) })}
          </p>
          <p className="text-xs text-gray-500">{t('cycleReports.followup.deloadNotCheckable')}</p>
        </div>
      )}
      {adherence.supplements?.length > 0 && (
        <div>
          <h5 className="font-medium text-gray-900">{t('cycleReports.followup.supplements')}</h5>
          <ul className="list-disc pl-5">
            {adherence.supplements.map((s) => (
              <li key={s.recommendation_key ?? s.subject}>
                {t('cycleReports.followup.supplementLine', {
                  subject: s.subject, from: num(s.recommended?.from), to: num(s.recommended?.to), unit: s.recommended?.unit ?? '',
                })}
                {(s.records ?? []).map((r, i) => (
                  <span key={i} className="block text-xs text-gray-500">
                    {r.end_date
                      ? t('cycleReports.followup.supplementEnded', { name: r.name, date: formatDate(r.end_date) })
                      : t('cycleReports.followup.supplementActive', { name: r.name, quantity: num(r.default_quantity), unit: r.quantity_unit ?? '' })}
                  </span>
                ))}
              </li>
            ))}
          </ul>
        </div>
      )}
      {adherence.pre_draw?.length > 0 && (
        <div>
          <h5 className="font-medium text-gray-900">{t('cycleReports.followup.preDraw')}</h5>
          <ul className="list-disc pl-5">
            {adherence.pre_draw.map((p) => (
              <li key={p.code} data-testid={`followup-predraw-${p.code}`}>
                {t(`cycleReports.followup.preDrawStep.${p.code}`)}: {t(`cycleReports.followup.preDrawStatus.${p.status}`)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Outcomes({ outcomes, markerNames = {} }) {
  const { t } = useTranslation()
  if (!outcomes.length) return <p className="text-sm text-gray-500">{t('cycleReports.followup.noRecommendations')}</p>
  return (
    <ul className="space-y-2 text-sm">
      {outcomes.map((o) => (
        <li key={o.key} className="border-t border-gray-100 pt-2" data-testid={`followup-outcome-${o.key}`}>
          <p className="font-medium text-gray-900">{o.title}</p>
          <p className="text-gray-700">
            {t(`cycleReports.followup.followed.${o.followed?.state ?? 'not_checkable'}`)}
            {' · '}
            <span className="font-medium">{t(`cycleReports.followup.attribution.${o.attribution}`)}</span>
          </p>
          <ul className="text-xs text-gray-600">
            {(o.target_markers ?? []).map((m) => (
              <li key={m.marker_code}>
                {m.marker_name ?? markerNames[m.marker_code] ?? humanizeCode(m.marker_code)}: {t(`cycleReports.followup.outcome.${m.label}`)}
                {m.change != null ? ` (${m.change > 0 ? '+' : ''}${m.change})` : ''}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}

// versionNumber: a past version's frozen follow-up; omitted = the current version.
export function FollowUpTab({ cycleId, versionNumber = null }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['health-cycle', cycleId, 'followup', versionNumber],
    queryFn: () => healthCyclesAPI.getFollowUp(cycleId, versionNumber).then((r) => r.data),
  })
  const choose = useMutation({
    mutationFn: (versionId) => healthCyclesAPI.chooseFollowUpBaseline(cycleId, versionId).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['health-cycle', cycleId, 'followup', versionNumber], data)
      queryClient.invalidateQueries({ queryKey: ['health-cycle', cycleId] })
    },
  })

  if (query.isLoading) return <p className="text-sm text-gray-500">{t('cycleReports.followup.loading')}</p>
  if (query.isError) {
    return <p className="text-sm text-red-600" role="alert">{t(`cycleReports.errors.${errorCode(query.error)}`, t('cycleReports.followup.error'))}</p>
  }
  const data = query.data ?? {}
  const result = data.result
  if (!result) {
    return (
      <div className="text-center py-10" data-testid="followup-empty">
        <p className="text-sm font-medium text-gray-700">
          {data.reason === 'no_finalized_baseline' ? t('cycleReports.followup.noBaseline') : t('cycleReports.followup.notYet')}
        </p>
        <p className="text-xs text-gray-500 mt-1">{t('cycleReports.followup.emptyHint')}</p>
      </div>
    )
  }
  const summary = result.summary ?? {}
  const options = data.baseline_options ?? []
  return (
    <div className="space-y-4" data-testid="followup-tab">
      <Card id="summary" title={t('cycleReports.followup.sections.summary')}>
        <p className="text-xs text-gray-500">
          {t('cycleReports.followup.period', { from: formatDate(result.baseline_test_date), to: formatDate(result.new_test_date) })}
        </p>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          {SUMMARY_KEYS.map((key) => (
            <div key={key} className="rounded bg-gray-50 p-2">
              <dt className="text-xs text-gray-500">{t(`cycleReports.followup.summary.${key}`)}</dt>
              <dd className="font-semibold text-gray-900" data-testid={`followup-summary-${key}`}>{num(summary[key])}</dd>
            </div>
          ))}
        </dl>
        {result.narrative?.text && <p className="text-sm text-gray-700">{result.narrative.text}</p>}
        {(result.notes ?? []).map((n) => (
          <div key={n.code} className="text-xs text-amber-700" data-testid={`followup-note-${n.code}`}>
            <p>{t(`cycleReports.followup.note.${n.code}`, { days: n.days })}</p>
            {(n.periods ?? []).length > 0 && (
              <ul className="list-disc pl-4">
                {n.periods.map((p) => (
                  <li key={`${p.goal_id}-${p.from}`}>
                    {t('cycleReports.followup.goalPeriod', {
                      from: formatDate(p.from), to: formatDate(p.until), count: p.days,
                      kcal: p.targets?.target_calories ?? '—',
                    })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {options.length > 1 && !versionNumber && (
          <label className="block text-sm text-gray-700">
            {t('cycleReports.followup.baseline')}
            <select
              className="input mt-1"
              value={result.baseline?.version_id ?? ''}
              disabled={choose.isPending}
              onChange={(e) => choose.mutate(Number(e.target.value))}
              data-testid="followup-baseline-select"
            >
              {options.map((o) => (
                <option key={o.version_id} value={o.version_id}>
                  {t('cycleReports.followup.baselineOption', { date: formatDate(o.blood_test_date), number: o.number })}
                </option>
              ))}
            </select>
          </label>
        )}
        {choose.isError && <p className="text-sm text-red-600" role="alert">{t('cycleReports.followup.error')}</p>}
      </Card>
      <Card id="markers" title={t('cycleReports.followup.sections.markers')}>
        <Markers markers={result.markers ?? []} />
      </Card>
      <Card id="adherence" title={t('cycleReports.followup.sections.adherence')}>
        <Adherence adherence={result.adherence ?? {}} />
        {Object.keys(result.errors ?? {}).length > 0 && (
          <p className="text-xs text-red-600">{t('cycleReports.followup.partialError')}</p>
        )}
      </Card>
      <Card id="outcomes" title={t('cycleReports.followup.sections.outcomes')}>
        <Outcomes outcomes={result.outcomes ?? []} markerNames={Object.fromEntries((result.markers ?? []).map((m) => [m.marker_code, m.marker_name ?? humanizeCode(m.marker_code)]))} />
      </Card>
    </div>
  )
}

export default FollowUpTab
